import type { UserExperiment } from '@otter-ai/recall-client';
import {
  logExperimentCohort,
  logExperimentCohortOnInit,
} from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { loginInfoQueryCreator } from '@/query/loginInfoQueryCreator';
import { userExperimentsQuery } from './experiments';

/**
 * Include near the top of the React tree to fetch experiments.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <ProviderA>
 *       <>
 *         <ExperimentClientInitializer />
 *         <MainContent />
 *       </>
 *     </ProviderA>
 *   );
 * }
 */
export function ExperimentClientInitializer() {
  const { data: loginData } = useQuery(loginInfoQueryCreator());
  const isLoggedIn = loginData?.['logged-in'] === true;
  const { data: experimentsData } = useQuery({
    ...userExperimentsQuery,
    enabled: isLoggedIn,
  });

  useEffect(() => {
    if (!experimentsData) return;
    logExperimentCohortOnInit(
      Array.from(experimentsData.entries()).map(([key, data]) => ({
        key,
        cohort: data.group,
      })),
    );
  }, [experimentsData]);

  return null;
}

/**
 * Hook to get experiment data
 *
 * @example
 * ```tsx
 * function Example() {
 *   const exp = useExperiment('dynamic-chat');
 * }
 */
export function useExperiment(
  experiment: string,
  options?: { enabled?: boolean },
): UserExperiment {
  const { data } = useQuery({
    ...userExperimentsQuery,
    ...options,
  });
  const exp = data?.get(experiment) ?? { experiment, group: '', config: {} };
  logExperimentCohort({ key: experiment, cohort: exp.group });
  return exp;
}

/**
 * Hook to test if a user is assigned to 'enabled', 'on' or 'treatment' group for the experiment
 *
 * @example
 * ```tsx
 * function Example() {
 *   const featureEnabled = useExperimentEnabled('new-feature');
 *   // ...
 * }
 * ```
 */
export function useExperimentEnabled(
  experiment: string,
  options?: { enabled?: boolean },
): boolean {
  return useExperimentInGroup(
    experiment,
    ['on', 'enabled', 'treatment'],
    options,
  );
}
/**
 * Hook to test if the user is assigned to one of the groups in an experiment
 *
 * @example
 * ```tsx
 * function Example() {
 *   const featureV4 = useExperimentInGroup('new-feature', 'v4');
 *   // ...
 * }
 * ```
 */
export function useExperimentInGroup(
  experiment: string,
  group: string | string[],
  options?: { enabled?: boolean },
): boolean {
  const exp = useExperiment(experiment, options);

  if (exp.group === null) {
    return false;
  }

  if (Array.isArray(group)) {
    return group.includes(exp.group);
  }

  return group === exp.group;
}

/**
 * Hook to test if the experiments are loading
 *
 * @example
 * ```tsx
 * function Example() {
 *   const experimentsLoading = useIsExperimentsLoading();
 *   const exp = useExperiment('new-feature');
 *   if (experimentsLoading) {
 *     return <div>Loading...</div>;
 *   }
 *   return <div>{exp.group}</div>;
 * }
 * ```
 */
export function useIsExperimentsLoading(options?: { enabled?: boolean }) {
  const { isLoading } = useQuery({ ...userExperimentsQuery, ...options });
  return isLoading;
}
